import { useEffect, useRef, useState } from 'react';
import { cyclesAPI } from '../../../api/cycles';

// Fetch the STORED cycle window per side for (symbol, timeframe) — the rows
// dwlf-scheduled-jobs folds from the confirmed-pivot ledger and SPT projects
// through GET /v2/cycles/windows (DWLF-113 ①). The chart draws exactly these
// dates; it no longer computes a band of its own from visible pivots, and it
// no longer turns quantile bar-counts into dates itself.
//
// Returns { windows, loading, error }:
//   windows = { low: CycleWindow | null, high: CycleWindow | null,
//               verification: { state, reason } } | null
// A side is null when the store holds nothing for it (a symbol with no
// backfill yet, or a timeframe the store does not cover — hourly).
// The caller renders an explicit "no window" state for that; a fetch error
// leaves `windows` null and `error` set, and the chart draws nothing.
//
// One fetch per (symbol, timeframe). No polling — a window moves only when
// a new pivot is confirmed, which is a slow event on every timeframe.
export const useCycleWindows = ({ symbol, timeframe, enabled = true }) => {
  const [windows, setWindows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sequence ref so a stale (symbol, tf) response can't overwrite a newer one.
  const requestSeqRef = useRef(0);

  // Reset when the identity changes so the previous symbol's band is never
  // drawn on the new symbol while the fetch is in flight. Toggling `enabled`
  // is deliberately not a trigger (Tune must not discard the data).
  useEffect(() => {
    setWindows(null);
  }, [symbol, timeframe]);

  useEffect(() => {
    if (!enabled || !symbol || !timeframe) {
      setLoading(false);
      setError(null);
      return undefined;
    }
    const seq = ++requestSeqRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // `depth`: each window carries its derived price zone (DWLF-416), drawn beside the timing band.
    cyclesAPI.getWindows({ symbol, timeframe, depth: true })
      .then((data) => {
        if (cancelled || seq !== requestSeqRef.current) {
          return;
        }
        setWindows({
          low: data?.low?.current ?? null,
          high: data?.high?.current ?? null,
          verification: data?.verification ?? null,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || seq !== requestSeqRef.current) {
          return;
        }
        // Fail visibly, not silently: no band, and the page says why.
        setError(err?.message || 'cycle windows fetch failed');
        setWindows(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe, enabled]);

  // `depthRequested`: this hook always asks for each window's price band, and says so, so the page's
  // "zone not drawn" sentence reads the fact rather than restating it.
  return { windows, loading, error, depthRequested: true };
};

export default useCycleWindows;
